import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  CircularProgress,
  Typography,
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import api from '../api';

interface Customer {
  tenantID: string;
  companyName: string;
  isActive: boolean;
}

interface SuspendActivateCustomerDialogProps {
  open: boolean;
  customer: Customer | null;
  onClose: () => void;
  onSuccess: () => void;
}

const SuspendActivateCustomerDialog: React.FC<SuspendActivateCustomerDialogProps> = ({
  open,
  customer,
  onClose,
  onSuccess,
}) => {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  const isSuspending = customer?.isActive ?? false;

  const handleReasonChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setReason(e.target.value);
    if (validationError) {
      setValidationError(null);
    }
  };

  const handleSubmit = async () => {
    if (!customer) return;

    // Validate reason
    if (!reason.trim()) {
      setValidationError('Reason is required');
      return;
    }

    if (reason.trim().length < 10) {
      setValidationError('Reason must be at least 10 characters');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await api.post(
        `/TCAdminCustomerStatus/${customer.tenantID}`,
        {
          isActive: !isSuspending,
          reason: reason.trim(),
        }
      );

      onSuccess();
      handleClose();
    } catch (err: any) {
      console.error('Error updating customer status:', err);
      console.error('Error response:', err.response);
      console.error('Error data:', err.response?.data);
      console.error('Error status:', err.response?.status);

      let errorMessage = `Failed to ${isSuspending ? 'suspend' : 'activate'} customer. Please try again.`;
      if (err.response?.data) {
        if (typeof err.response.data === 'string') {
          errorMessage = err.response.data;
        } else if (err.response.data.message) {
          errorMessage = err.response.data.message;
        }
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setReason('');
      setValidationError(null);
      setError(null);
      onClose();
    }
  };

  if (!customer) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isSuspending ? (
            <WarningIcon color="error" />
          ) : (
            <CheckCircleIcon color="success" />
          )}
          <span>
            {isSuspending ? 'Suspend' : 'Activate'} Customer
          </span>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Typography variant="body1">
            {isSuspending ? (
              <>
                You are about to <strong>suspend</strong> <strong>{customer.companyName}</strong>.
                <br />
                <br />
                Suspended customers will not be able to access their services until reactivated.
              </>
            ) : (
              <>
                You are about to <strong>activate</strong> <strong>{customer.companyName}</strong>.
                <br />
                <br />
                The customer will regain access to their services immediately.
              </>
            )}
          </Typography>

          <TextField
            label="Reason"
            value={reason}
            onChange={handleReasonChange}
            error={!!validationError}
            helperText={validationError || 'Please provide a reason for this action (minimum 10 characters)'}
            multiline
            rows={3}
            fullWidth
            required
            disabled={loading}
            placeholder={
              isSuspending
                ? 'e.g., Non-payment, policy violation, customer request'
                : 'e.g., Payment received, issue resolved, subscription renewed'
            }
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color={isSuspending ? 'error' : 'success'}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading
            ? `${isSuspending ? 'Suspending' : 'Activating'}...`
            : `${isSuspending ? 'Suspend' : 'Activate'} Customer`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SuspendActivateCustomerDialog;
